// Build service URLs from a single domain instead of injecting one env var per endpoint.
// Mirrors how environment/endpoints.tf derives them:
//   public:  https://<subdomain>.<domain>
//   private: https://<subdomain>.private.<domain>
// The enum values ARE the subdomains, so construction is a direct interpolation and the
// caller references services in a type-safe way instead of by string. This module is the
// single source of truth for service subdomains across the monorepo.
// Private (service-to-service) hosts. Values mirror environment/endpoints.tf
// `private_endpoint_services`.
export var ServiceType;
(function (ServiceType) {
    ServiceType["ACTIVITY_METRIC"] = "activity-metric";
    ServiceType["ALCHEMY_BRIDGE"] = "alchemy-bridge";
    ServiceType["API_KEYS"] = "api-keys";
    ServiceType["AUDITING"] = "auditing";
    ServiceType["AUTHORIZER"] = "authorizer";
    ServiceType["BACKOFFICE_IAM"] = "backoffice-iam";
    ServiceType["BULK"] = "bulk";
    ServiceType["CERTIFICATE_REQUEST_MANAGER"] = "certs-req";
    ServiceType["CERTS_INDEX"] = "certs-index";
    ServiceType["CHANGE_REQUESTS"] = "change-requests";
    ServiceType["CONNECTORS"] = "connectors";
    ServiceType["CREDITS"] = "credits";
    ServiceType["CUSTODIAN"] = "custodian";
    ServiceType["CUSTOM_DOMAINS"] = "custom-domains";
    ServiceType["EXTERNAL_CREDENTIAL"] = "external-credential";
    ServiceType["IMAGE_GENERATOR"] = "images";
    ServiceType["IMAGE_STORE"] = "image-store";
    ServiceType["KYC"] = "kyc";
    ServiceType["LEARNING_ROUTES"] = "learning-routes";
    ServiceType["LINKEDIN_USERS"] = "linkedin-users";
    ServiceType["LTI"] = "lti";
    ServiceType["MARKETING"] = "marketing";
    ServiceType["MINTER"] = "minter";
    ServiceType["NOTIFIER"] = "notifier";
    ServiceType["PAGES"] = "pages";
    ServiceType["PREMIUM_FEATURES_MANAGER"] = "premium-features-manager";
    ServiceType["PROOFS_PROXIES"] = "proxy";
    ServiceType["RBAC"] = "rbac";
    ServiceType["REGION_DETECTOR"] = "region-detector";
    ServiceType["REPORTING"] = "reporting";
    ServiceType["SANA"] = "sana";
    ServiceType["SKILL_GRAPH"] = "skill-graph";
    ServiceType["SOCIAL_MEDIA_BRIDGE_API"] = "share";
    ServiceType["SOCIAL_MEDIA_BRIDGE_LINKEDIN"] = "linkedin";
    ServiceType["TEMPLATE_MANAGER"] = "templates";
    ServiceType["TRANSLATION_BRIDGE"] = "translation-bridge";
    ServiceType["WEBHOOKS"] = "webhooks";
})(ServiceType || (ServiceType = {}));
// Public-facing hosts. Their subdomains differ from both the service name and the private
// subdomain (e.g. certifier -> "mint", viewer-api -> "v"), so they need their own enum.
// Values mirror environment/endpoints.tf `public_endpoint_services`.
export var PublicService;
(function (PublicService) {
    PublicService["ALCHEMY_BRIDGE"] = "alchemy-bridge";
    PublicService["API"] = "api";
    PublicService["API_KEYS"] = "api-keys";
    PublicService["BACKOFFICE"] = "backoffice";
    PublicService["BACKOFFICE_API"] = "b";
    PublicService["CERTIFIER"] = "mint";
    PublicService["CERTIFIER_API"] = "c";
    PublicService["CREDENTIAL_LOOKUP"] = "lookup";
    PublicService["CREDENTIAL_LOOKUP_API"] = "l";
    PublicService["CREDITS"] = "credits";
    PublicService["DOCS"] = "docs";
    PublicService["HOLDER"] = "holder";
    PublicService["HOLDER_API"] = "h";
    PublicService["IMAGE_STORE"] = "image-store";
    PublicService["KYC"] = "kyc";
    PublicService["LTI"] = "lti";
    PublicService["LTI_FRONTEND"] = "lti-frontend";
    PublicService["MARKETING"] = "marketing";
    PublicService["MINTER"] = "minter";
    PublicService["RBAC"] = "rbac";
    PublicService["SOCIAL_MEDIA_BRIDGE"] = "share";
    PublicService["TEMPLATE_MANAGER"] = "templates";
    PublicService["VC_RESOLVER"] = "vc";
    PublicService["VIEWER"] = "view";
    PublicService["VIEWER_API"] = "v";
    PublicService["WEBHOOKS"] = "webhooks";
})(PublicService || (PublicService = {}));
export const serviceEndpoints = (domain, overrides) => ({
    privateUrl: (service) => overrides?.private?.[service] ?? `https://${service}.private.${domain}`,
    publicUrl: (service) => overrides?.public?.[service] ?? `https://${service}.${domain}`,
});
export const REVOCATION_LIST_PATH_PREFIX = '/revocation';
export const revocationListUrlFor = (viewerApiPublicRoot, organizationId) => `${viewerApiPublicRoot}${REVOCATION_LIST_PATH_PREFIX}/${organizationId}`;
export const revocationListUrl = (endpoints, organizationId) => revocationListUrlFor(endpoints.publicUrl(PublicService.VIEWER_API), organizationId);
// Builds endpoints from a Vite-style env object: derives from VITE_DOMAIN and, when present,
// applies the VITE_ENDPOINT_OVERRIDES JSON map (dev-server only).
export const serviceEndpointsFromEnv = (env) => serviceEndpoints(env.VITE_DOMAIN, env.VITE_ENDPOINT_OVERRIDES ? JSON.parse(env.VITE_ENDPOINT_OVERRIDES) : undefined);
//# sourceMappingURL=index.js.map